import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { Pagination } from '../components/Pagination';
import { discountPct, formatMoney, relativeTime } from '../lib/format';
import { useUrlFilters } from '../lib/useUrlFilters';

type SortOption = 'discount' | 'newest' | 'price_asc' | 'price_desc';

const PAGE_SIZE = 50;

export function Discounts() {
  const [params, setParams] = useUrlFilters();
  const category = params.get('category') ?? '';
  const sort = (params.get('sort') ?? 'discount') as SortOption;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.categories(),
    staleTime: 30 * 60_000,
  });

  const discounts = useQuery({
    queryKey: ['discounts', { category, sort, page }],
    queryFn: () => api.discounts({
      category: category || undefined,
      sort,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    staleTime: 10 * 60_000,
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <LoadingOverlay visible={discounts.isFetching} />
      <div className="toolbar">
        <select
          value={category}
          onChange={(e) => setParams({ category: e.target.value }, { resetPage: true })}
        >
          <option value="">Todas las categorías</option>
          {categories.data?.map((c) => (
            <option key={c.category} value={c.category}>
              {c.category} ({c.count})
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) =>
            setParams({ sort: e.target.value === 'discount' ? '' : e.target.value }, { resetPage: true })
          }
        >
          <option value="discount">Mayor descuento</option>
          <option value="newest">Más recientes</option>
          <option value="price_asc">Precio: menor a mayor</option>
          <option value="price_desc">Precio: mayor a menor</option>
        </select>
      </div>

      {discounts.isLoading && <div className="loading">Cargando…</div>}
      {discounts.isError && (
        <div className="error">Error: {(discounts.error as Error).message}</div>
      )}
      {discounts.data && discounts.data.items.length === 0 && (
        <div className="empty">Sin ofertas para este filtro.</div>
      )}

      <div className="grid">
        {discounts.data?.items.map((p) => {
          const pct = discountPct(p.current_price_cents, p.list_price_cents);
          return (
            <Link key={p.id} to={`/p/${p.id}`} className="card">
              {p.image_url ? (
                <img className="card-img" src={p.image_url} alt="" loading="lazy" />
              ) : (
                <div className="card-img-fallback">sin imagen</div>
              )}
              <div className="card-name" title={p.name}>
                {p.name}
              </div>
              <div className="card-price-row">
                <span className="card-price">{formatMoney(p.current_price_cents)}</span>
                {p.list_price_cents && p.list_price_cents !== p.current_price_cents && (
                  <span className="card-list">{formatMoney(p.list_price_cents)}</span>
                )}
                {pct != null && <span className="badge badge-discount">-{pct}%</span>}
              </div>
              <div className="card-foot">
                <span>{p.store_id}</span>
                {p.category_path?.[0] && <span>{p.category_path[0]}</span>}
              </div>
              <div className="card-seen">Visto {relativeTime(p.priced_at)}</div>
            </Link>
          );
        })}
      </div>

      {discounts.data && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={discounts.data.total}
          onChange={(p) => {
            setParams({ page: p === 1 ? '' : String(p) });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
    </>
  );
}
